import React, { useEffect, useRef } from "react";
import { Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { isWsdotClosure } from "./WsdotClosuresLayer";

const DOT_ORANGE = "#F77F00";
const ALERT_AMBER = "#FFB347";
const PASS_OPEN = "#4A9B6E";
const PASS_ISSUE = "#E63946";

const closureIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6));">
    <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="2" transform="rotate(45 12 12)"
      fill="${DOT_ORANGE}" stroke="#0A0D0C" stroke-width="1.5"/>
    <rect x="10.9" y="6.5" width="2.2" height="7" rx="1" fill="#0A0D0C"/>
    <circle cx="12" cy="16.4" r="1.4" fill="#0A0D0C"/>
  </svg>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -11],
});

const alertIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6));">
    <rect x="4.8" y="4.8" width="14.4" height="14.4" rx="2" transform="rotate(45 12 12)"
      fill="${ALERT_AMBER}" stroke="#0A0D0C" stroke-width="1.6"/>
    <rect x="10.9" y="7" width="2.2" height="6.4" rx="1" fill="#0A0D0C"/>
    <circle cx="12" cy="16" r="1.35" fill="#0A0D0C"/>
  </svg>`,
  iconSize: [19, 19],
  iconAnchor: [10, 10],
  popupAnchor: [0, -9],
});

const passIcon = (issue) =>
  L.divIcon({
    className: "",
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6));">
      <circle cx="12" cy="12" r="10" fill="${issue ? PASS_ISSUE : PASS_OPEN}" stroke="#0A0D0C" stroke-width="1.5"/>
      <path d="M5.5 16 L10 8.5 L12.6 12.6 L14.4 10 L18.5 16 Z" fill="#FFFFFF"/>
    </svg>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -11],
  });

const hasRestriction = (p) =>
  /closed|restrict|chain|traction|advisory/i.test(
    `${p.road_condition || ""} ${p.restriction_north || ""} ${p.restriction_south || ""} ${p.travel_advisory || ""}`
  );

function mpRange(a) {
  if (a.start_milepost == null) return null;
  const s = Math.round(a.start_milepost * 10) / 10;
  if (a.end_milepost != null && a.end_milepost !== a.start_milepost) {
    return `MP ${s} – ${Math.round(a.end_milepost * 10) / 10}`;
  }
  return `MP ${s}`;
}

export default function DotAlertsLayer({ alerts = [], passes = [], focusWsdot = null }) {
  const markerRefs = useRef({});
  useEffect(() => {
    if (!focusWsdot?.id) return;
    const t = setTimeout(() => markerRefs.current[focusWsdot.id]?.openPopup(), 900);
    return () => clearTimeout(t);
  }, [focusWsdot]);

  const placed = alerts.filter((a) => a.start_lat != null && a.start_lon != null);

  return (
    <>
      {placed.map((a) => {
        const closure = isWsdotClosure(a);
        const color = closure ? DOT_ORANGE : ALERT_AMBER;
        return (
          <React.Fragment key={`dot-${a.id}`}>
            {!!a.end_lat && !!a.end_lon && (a.end_lat !== a.start_lat || a.end_lon !== a.start_lon) && (
              <Polyline
                positions={[[a.start_lat, a.start_lon], [a.end_lat, a.end_lon]]}
                pathOptions={{ color, weight: 4, opacity: 0.65, dashArray: "8 6" }}
              />
            )}
            <Marker
              position={[a.start_lat, a.start_lon]}
              icon={closure ? closureIcon : alertIcon}
              data-testid={`dot-alert-marker-${a.id}`}
              ref={(r) => { markerRefs.current[a.id] = r; }}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 700, color, fontSize: 12 }}>
                    WSDOT · {(a.category || (closure ? "CLOSURE" : "ALERT")).toUpperCase()}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, margin: "3px 0" }}>
                    {a.road ? `SR ${a.road}` : "State highway"}
                    {mpRange(a) ? ` · ${mpRange(a)}` : ""}
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.45 }}>{a.headline}</div>
                  {a.priority && (
                    <div style={{ fontSize: 10, opacity: 0.65, marginTop: 4 }}>
                      Priority: {a.priority}
                      {a.last_updated ? ` · updated ${new Date(a.last_updated).toLocaleDateString()}` : ""}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          </React.Fragment>
        );
      })}
      {passes
        .filter((p) => p.latitude != null && p.longitude != null)
        .map((p) => {
          const issue = hasRestriction(p);
          return (
            <Marker
              key={`pass-${p.id}`}
              position={[p.latitude, p.longitude]}
              icon={passIcon(issue)}
              data-testid={`pass-marker-${p.id}`}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 700, color: issue ? PASS_ISSUE : PASS_OPEN, fontSize: 12 }}>
                    MOUNTAIN PASS{issue ? " · RESTRICTION" : " · OPEN"}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, margin: "3px 0" }}>
                    {p.name}{p.elevation_ft ? ` · ${p.elevation_ft.toLocaleString()} ft` : ""}
                  </div>
                  {p.road_condition && (
                    <div style={{ fontSize: 12, lineHeight: 1.45 }}>{p.road_condition}</div>
                  )}
                  {(p.restriction_north || p.restriction_south) && (
                    <div style={{ fontSize: 11, lineHeight: 1.4, marginTop: 3 }}>
                      {p.restriction_north && <div>NB: {p.restriction_north}</div>}
                      {p.restriction_south && <div>SB: {p.restriction_south}</div>}
                    </div>
                  )}
                  <div style={{ fontSize: 10, opacity: 0.65, marginTop: 4 }}>
                    {p.weather ? `${p.weather} · ` : ""}
                    {p.temperature_f != null ? `${Math.round(p.temperature_f)}°F · ` : ""}
                    {p.last_updated ? `updated ${new Date(p.last_updated).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
    </>
  );
}
